import { useState, useEffect, useCallback, useMemo } from "react";
import ReactSelect from "react-select";
import api from "api/api";

// redux
import { useSelector, useDispatch } from "react-redux";
import {
    loadOverallAnalysisTypeValueState,
    loadSecondaryAnalysisTypeValueState,
    loadAgreementAnalysisTypeValueState,
    loadClaimsAnalysisTypeValueState,
    loadOverallBoxAnalysisSelectionState,
    loadSecondaryBoxAnalysisSelectionState,
    loadAgreementBoxAnalysisSelectionState,
    loadClaimsBoxAnalysisSelectionState,
    loadOverallBoxAnalysisTypeSelectionState,
    loadSecondaryBoxAnalysisTypeSelectionState,
    loadAgreementBoxAnalysisTypeSelectionState,
    loadClaimsBoxAnalysisTypeSelectionState,
    loadOverallBoxAnalysisApplyAllSelectionState,
    loadSecondaryBoxAnalysisApplyAllSelectionState,
    loadAgreementBoxAnalysisApplyAllSelectionState,
    loadClaimsBoxAnalysisApplyAllSelectionState,
    loadFragranceOverallBoxAnalysisSelectionState,
    loadFragranceOverallBoxAnalysisTypeSelectionState,
    loadFragranceOverallTypeValueState,
    loadFragranceOverallBoxAnalysisApplyAllSelectionState,
    loadFragranceAdditionalBoxAnalysisApplyAllSelectionState,
    loadFragranceAdditionalBoxAnalysisSelectionState,
    loadFragranceAdditionalBoxAnalysisTypeSelectionState,
    loadFragranceAdditionalTypeValueState,
    loadExpressCltOverallBoxAnalysisSelectionState,
    loadExpressCltOverallBoxAnalysisTypeSelectionState,
    loadExpressCltOverallTypeValueState,
    loadExpressCltOverallBoxAnalysisApplyAllSelectionState,
    loadExpressCltAdditionalBoxAnalysisApplyAllSelectionState,
    loadExpressCltAdditionalBoxAnalysisSelectionState,
    loadExpressCltAdditionalBoxAnalysisTypeSelectionState,
    loadExpressCltAdditionalTypeValueState,
    // loadOverallDataViewOptionsState,
    // loadSecondaryDataViewOptionsState,
    // loadAgreementDataViewOptionsState,
    // loadClaimsDataViewOptionsState,
} from "store/features/analysisTypeSelectionSlice";

//Props Validations
import PropTypes from "prop-types";

//Material UI Components
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import ListItemText from "@mui/material/ListItemText";
import MuiSelect from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";

import { showNotification } from "store/features/notificationSlice";

// import mui icons
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

// select styles
import ReactSelectStyles from "../../styles/ReactSelectStyles";

// import components
import OverlappingScales from "./analysisModule/boxAnalysis/OverlappingScales";

// utils
import GetMacroCategoryOrder from "../../utils/GetMacroCategoryOrder";
import { getSortIndex, getAlphabeticalMacroCategories } from "../../utils/analysisTable/analysisUtils";

const customStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: "30px",
        height: "30px",
        padding: 0,
        margin: 0,
        border: "none",
        borderRadius: "10px",
        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
        "&:hover": {
            border: "none",
            borderRadius: "10px",
        },
    }),
    valueContainer: (base) => ({
        ...base,
        padding: "0 6px",
        margin: 0,
        fontSize: "10px",
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        fontSize: "11px",
    }),
    singleValue: (base) => ({
        ...base,
        padding: 0,
        margin: 0,
    }),
    menu: (provided) => ({
        ...provided,
        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
    }),
    option: (base) => ({
        ...base,
        padding: "5px 10px",
        fontSize: "10px",
        cursor: "pointer",
    }),
    clearIndicator: (base) => ({
        ...base,
        paddingRight: 0,
        paddingLeft: 1,
    }),
    dropdownIndicator: (base) => ({
        ...base,
        paddingLeft: 2,
        paddingRight: 2,
    }),
};

const AnalysisTypeOptions = [
    { value: "mean", label: "Mean" },
    { value: "box_analysis", label: "Box Analysis" },
];

// Box Analysis Type Options
const BoxAnalysisOptions = ["Top Box", "Bottom Box"];

const AnalysisTypeSelector = ({ value = "", pageName = "", variableCategory = "", mappings = [], handleFilterSubmit = () => { }, moduleName = "overall", clearResult = () => { }, disabled = false }) => {
    const dispatch = useDispatch();

    const [isOpen, setIsOpen] = useState(false);
    const [analysisType, setAnalysisType] = useState({});
    const [selectedOptions, setSelectedOptions] = useState({});

    const [applyAllStates, setApplyAllStates] = useState({});

    const [groupedVariables, setGroupedVariables] = useState({}); // api response
    const [selectedBoxAnalysisType, setSelectedBoxAnalysisType] = useState([]);

    const [prevSelectedBV, setPrevSelectedBV] = useState({});
    const [disableGetResultsFlag, setDisableGetResultsFlag] = useState(true);

    const productCategoryData = useSelector((state) => state.settingsStore.productCategory);
    const currentFilterStoreData = useSelector((state) => state.consumerFilterStore.currentSelectedFilters);
    const missingFilterStoreData = useSelector((state) => state.consumerFilterStore.missingFilters);
    const selectedStudies = useSelector((state) => state.studyDataStore.selectedStudies);

    const allAnalysisData = useSelector((state) => state.analysisResultsStore);

    const overallBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.overallBoxAnalysisSelection);
    const overallBoxAnalysisApplyAllSelection = useSelector((state) => state.analysisTypeSelectionStore.overallBoxAnalysisApplyAllSelection);
    const secondaryBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.secondaryBoxAnalysisSelection);
    const secondaryBoxAnalysisApplyAllSelection = useSelector((state) => state.analysisTypeSelectionStore.secondaryBoxAnalysisApplyAllSelection);
    const agreementBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.agreementBoxAnalysisSelection);
    const agreementBoxAnalysisApplyAllSelection = useSelector((state) => state.analysisTypeSelectionStore.agreementBoxAnalysisApplyAllSelection);
    const claimsBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.claimsBoxAnalysisSelection);
    const claimsBoxAnalysisApplyAllSelection = useSelector((state) => state.analysisTypeSelectionStore.claimsBoxAnalysisApplyAllSelection);
    const selectedStudy = useSelector((state) => state.studyDataStore.selectedStudies);

    //clt
    const fragranceOverallBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.fragranceOverallBoxAnalysisSelection);
    const fragranceOverallBoxAnalysisApplyAllSelection = useSelector((state) => state.analysisTypeSelectionStore.fragranceOverallBoxAnalysisApplyAllSelection);
    const fragranceAdditionalBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.fragranceAdditionalBoxAnalysisSelection);
    const fragranceAdditionalBoxAnalysisApplyAllSelection = useSelector((state) => state.analysisTypeSelectionStore.fragranceAdditionalBoxAnalysisApplyAllSelection);
    const productsSecondaryDataState = useSelector((state) => state.analysisResultsStore.fragranceMetricsAnalysisResult);

    //express clt
    const expressCltOverallBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.expressCltOverallBoxAnalysisSelection);
    const expressCltOverallBoxAnalysisApplyAllSelection = useSelector((state) => state.analysisTypeSelectionStore.expressCltOverallBoxAnalysisApplyAllSelection);
    const expressCltAdditionalBoxAnalysisSelection = useSelector((state) => state.analysisTypeSelectionStore.expressCltAdditionalBoxAnalysisSelection);
    const expressCltAdditionalBoxAnalysisApplyAllSelection = useSelector((state) => state.analysisTypeSelectionStore.expressCltAdditionalBoxAnalysisApplyAllSelection);

    const filterSelectionStatus = useSelector((state) => state.consumerFilterStore.filterSelectionStatus);
    const removedKpiStoreState = useSelector((state) => state.analysisKpiStore[`${moduleName}RemovedKpiList`]);
    const flatRemovedKpiList = useMemo(() => removedKpiStoreState.flatMap((item) => item.split(",")), [removedKpiStoreState]);

    const isUnileverFragranceHouse = process.env?.REACT_APP_UNILEVER_FRAGRANCE_HOUSE?.toLowerCase() === productCategoryData?.label?.toLowerCase();

    const productCategoryId = productCategoryData?.value || null;
    const coreDataIds = useMemo(() => selectedStudies.map((datum) => datum.id), [selectedStudies]);
    const [noteFlag, setNoteFlag] = useState("");
    const expressCLTAdditionalAnalysisCategories = ["Overall Fragrance", "Fragrance (Secondary)", "Sensory & Application"];
    const expressCLTOverallAnalysisCategories = ["Overall Fragrance", "Concept"];
    const cltAdditionalAnalysisCategories = isUnileverFragranceHouse ? ["Overall Opinion", "Fragrance Characteristics", "Fragrance Fitment"] : ["Overall Fragrance", "Fragrance (Secondary)", "Fragrance (JAR)"];
    const cltOverallAnalysisCategories = isUnileverFragranceHouse ? ["Overall Fragrance", "Overall Opinion"] : ["Overall Fragrance"];

    let customOrderMacroCategory = GetMacroCategoryOrder(productCategoryData?.label?.toLowerCase(), moduleName);
    if (!customOrderMacroCategory && moduleName !== "claims") {
        customOrderMacroCategory = getAlphabeticalMacroCategories(mappings);
    }

    useEffect(() => {
        setPrevSelectedBV({});
        if (pageName === "overallKpiAnalysis") {
            setSelectedOptions(overallBoxAnalysisSelection);
            setApplyAllStates(overallBoxAnalysisApplyAllSelection);
        } else if (pageName === "secondaryAttributeAnalysis") {
            setSelectedOptions(secondaryBoxAnalysisSelection);
            setApplyAllStates(secondaryBoxAnalysisApplyAllSelection);
        } else if (pageName === "agreementKpiAnalysis") {
            setSelectedOptions(agreementBoxAnalysisSelection);
            setApplyAllStates(agreementBoxAnalysisApplyAllSelection);
        } else if (pageName === "claimsAnalysis") {
            setSelectedOptions(claimsBoxAnalysisSelection);
            setApplyAllStates(claimsBoxAnalysisApplyAllSelection);
        } else if (pageName === "fragranceOverallAnalysis") {
            setSelectedOptions(fragranceOverallBoxAnalysisSelection);
            setApplyAllStates(fragranceOverallBoxAnalysisApplyAllSelection);
        } else if (pageName === "fragranceAdditionalAnalysis") {
            setSelectedOptions(fragranceAdditionalBoxAnalysisSelection);
            setApplyAllStates(fragranceAdditionalBoxAnalysisApplyAllSelection);
        } else if (pageName === "expressCLTOverallAnalysis") {
            setSelectedOptions(expressCltOverallBoxAnalysisSelection);
            setApplyAllStates(expressCltOverallBoxAnalysisApplyAllSelection);
        } else if (pageName === "expressCLTAdditionalAnalysis") {
            setSelectedOptions(expressCltAdditionalBoxAnalysisSelection);
            setApplyAllStates(expressCltAdditionalBoxAnalysisApplyAllSelection);
        }
    }, [
        pageName,
        overallBoxAnalysisSelection,
        secondaryBoxAnalysisSelection,
        agreementBoxAnalysisSelection,
        claimsBoxAnalysisSelection,
        fragranceOverallBoxAnalysisSelection,
        fragranceAdditionalBoxAnalysisSelection,
        fragranceAdditionalBoxAnalysisApplyAllSelection,
        overallBoxAnalysisApplyAllSelection,
        secondaryBoxAnalysisApplyAllSelection,
        agreementBoxAnalysisApplyAllSelection,
        claimsBoxAnalysisApplyAllSelection,
        fragranceOverallBoxAnalysisApplyAllSelection,
        expressCltAdditionalBoxAnalysisApplyAllSelection,
        expressCltOverallBoxAnalysisApplyAllSelection,
        expressCltOverallBoxAnalysisSelection,
        expressCltAdditionalBoxAnalysisSelection,
    ]);

    const macroCategoryFlag = pageName === "secondaryAttributeAnalysis" || pageName === "agreementKpiAnalysis" || pageName === "fragranceOverallAnalysis" || pageName === "fragranceAdditionalAnalysis" || pageName === "expressCLTOverallAnalysis" || pageName === "expressCLTAdditionalAnalysis";
    const allSelected = selectedBoxAnalysisType.length === BoxAnalysisOptions.length;
    let boxGridFlag;
    if (selectedBoxAnalysisType.length === 1 && macroCategoryFlag) {
        boxGridFlag = 4;
    } else if (selectedBoxAnalysisType.length === 1 && !macroCategoryFlag) {
        boxGridFlag = 6;
    } else if (selectedBoxAnalysisType.length === 2 && macroCategoryFlag) {
        boxGridFlag = 2;
    } else {
        boxGridFlag = 3;
    }

    const filterByMacroCategories = (data, categories) => Object.fromEntries(Object.entries(data).map(([key, arr]) => [key, Array.isArray(arr) ? arr.filter((item) => categories.includes(item.macro_category)) : arr]));

    const fetchVariablesData = useCallback(async () => {
        try {
            const selectedStudyTypeIds = [...new Set(selectedStudy.map((datum) => datum.study_type))].join(",");
            const selectedStudyTypeMethodIds = [...new Set(selectedStudy.map((datum) => datum.study_type_method))].join(",");
            let url = process.env.REACT_APP_SERVER_PATH + `/data-mappings/data-view-options/?archive=false&variable_category=${encodeURIComponent(variableCategory)}&core_data_model=${coreDataIds}`;
            if (productCategoryId) {
                url += `&product_category=${productCategoryId}&study_type=${selectedStudyTypeIds}&study_type_method=${selectedStudyTypeMethodIds}`;
            }
            await api
                .get(url)
                .then((res) => {
                    let data = res.data;
                    if (pageName === "expressCLTAdditionalAnalysis") {
                        data = filterByMacroCategories(data, expressCLTAdditionalAnalysisCategories);
                    } else if (pageName === "expressCLTOverallAnalysis") {
                        data = filterByMacroCategories(data, expressCLTOverallAnalysisCategories);
                    }
                    if (pageName === "fragranceOverallAnalysis") {
                        data = filterByMacroCategories(data, cltOverallAnalysisCategories);
                    }
                    if (pageName === "fragranceAdditionalAnalysis") {
                        data = filterByMacroCategories(data, cltAdditionalAnalysisCategories);
                    }
                    setGroupedVariables(data);
                })
                .catch((error) => {
                    handleError("error", error);
                });
        } catch (error) {
            handleError("error", error);
        }
    }, [variableCategory, coreDataIds, productCategoryId]);

    const setBoxSelectionOption = (selectedOption) => {
        if (pageName === "overallKpiAnalysis") {
            dispatch(loadOverallAnalysisTypeValueState(selectedOption));
        } else if (pageName === "secondaryAttributeAnalysis") {
            dispatch(loadSecondaryAnalysisTypeValueState(selectedOption));
        } else if (pageName === "agreementKpiAnalysis") {
            dispatch(loadAgreementAnalysisTypeValueState(selectedOption));
        } else if (pageName === "claimsAnalysis") {
            dispatch(loadClaimsAnalysisTypeValueState(selectedOption));
        } else if (pageName === "fragranceOverallAnalysis") {
            dispatch(loadFragranceOverallTypeValueState(selectedOption));
        } else if (pageName === "fragranceAdditionalAnalysis") {
            dispatch(loadFragranceAdditionalTypeValueState(selectedOption));
        } else if (pageName === "expressCLTOverallAnalysis") {
            dispatch(loadExpressCltOverallTypeValueState(selectedOption));
        } else if (pageName === "expressCLTAdditionalAnalysis") {
            dispatch(loadExpressCltAdditionalTypeValueState(selectedOption));
        }
    };

    const handleChange = (selectedOption) => {
        if (value?.value === "box_analysis" && selectedOption.value === "mean") {
            clearResult();
        }
        if (selectedOption.value === "box_analysis") {
            setIsOpen(true);
        } else {
            setBoxSelectionOption(selectedOption);
        }
        setAnalysisType(selectedOption);
    };

    useEffect(() => {
        fetchVariablesData();
    }, [fetchVariablesData]);

    const checkSecondaryData = (removeOverallfromMapping) => {
        if (!removeOverallfromMapping.length) {
            return false;
        }

        // const checkTopBox = analysisType.includes("Top Box");
        // const checkBottomBox = analysisType.includes("Bottom Box");

        let hasData = false;
        removeOverallfromMapping[0].displayLabels.forEach((ele) => {
            let productWithNA = 0;
            productsSecondaryDataState.forEach((datum) => {
                const scores = datum.scores?.find((item) => item.backendVariable === ele.backendVariable);
                // if (checkTopBox && checkBottomBox) {
                //     if (!scores?.top_per && !scores?.bottom_per) {
                //         productWithNA +=1;
                //     }
                    
                // }
                // if (checkTopBox && !checkBottomBox) {
                //     if (!scores?.top_per) {
                //         productWithNA +=1;
                //     }
                // }
                // if (!checkTopBox && checkBottomBox) {
                //     if (!scores?.bottom_per) {
                //         productWithNA +=1;
                //     }
                // }
                if (!scores?.mean) {
                    productWithNA +=1;
                }
            });
            if (productWithNA !== productsSecondaryDataState.length) {
                hasData = true;
                return hasData;
            }
        });
        return hasData;
    };
   
    useEffect(() => {
        const isJarPresent =
            moduleName !== "claims" &&
            mappings &&
            mappings.length > 0 &&
            mappings.some((category) => category.macroCategory === "Fragrance (JAR)");
        
        let enableGetResultforJar = false;
        if (isJarPresent) {
            // Remove Jar from mappings
            // Store it in a variable and check if fragrance overall enable or not
            // if yes then show get result button 
            // if no then check fragrance secondare present or not 
            // if fragrance secondary with data  is present disable get result button else enable getResult
            const removeJarfromMapping = mappings.filter(ele => !ele.macroCategory.includes("JAR"))
            const variableForSorting = isUnileverFragranceHouse ? "R_OOF" : "R_OO_Frag_10pt";
            const checkOverallVariable = removedKpiStoreState.includes(variableForSorting)
            if (!checkOverallVariable) {
                enableGetResultforJar = true;
            } else {
                const removeOverallfromMapping = removeJarfromMapping.filter(ele => !ele.macroCategory.includes("Overall"))
                const hasSecondaryData = checkSecondaryData(removeOverallfromMapping);
                enableGetResultforJar = !hasSecondaryData;

            }

        } else {
            enableGetResultforJar = false;
        }
        if (Object.keys(selectedOptions).length) {
            const hasTopBox = Object.values(selectedOptions).some((item) => "top_box" in item);
            const hasBottomBox = Object.values(selectedOptions).some((item) => "bottom_box" in item);
            if (selectedBoxAnalysisType.length === 0) {
                if (hasTopBox && hasBottomBox) {
                    setSelectedBoxAnalysisType(["Top Box", "Bottom Box"]);
                } else if (hasTopBox && !hasBottomBox) {
                    setSelectedBoxAnalysisType(["Top Box"]);
                } else if (!hasTopBox && hasBottomBox) {
                    setSelectedBoxAnalysisType(["Bottom Box"]);
                } else {
                    setSelectedBoxAnalysisType([]);
                }
            }
            const hasSelection = Object.values(selectedOptions).some((item) => Object.keys(item).length && (item?.top_box || item?.bottom_box));
            setDisableGetResultsFlag(!hasSelection);
        } else {
            
            if (pageName === "fragranceAdditionalAnalysis" && enableGetResultforJar) {
                setDisableGetResultsFlag(false);
            } else {
                // setSelectedBoxAnalysisType([]);
                setDisableGetResultsFlag(true);
            }
            
        }
    }, [selectedOptions, selectedBoxAnalysisType, pageName, mappings]);

    const handleError = (type, error) => {
        dispatch(showNotification({ severity: type, data: error }));
    };

    const handleBoxAnalysisTypeChange = (event) => {
        const value = event.target.value;
        if ((value.includes("Top Box") && value.includes("Bottom Box")) || value.includes("all")) {
            if (selectedBoxAnalysisType.length === BoxAnalysisOptions.length) {
                setSelectedBoxAnalysisType([]);
            } else {
                setSelectedBoxAnalysisType(BoxAnalysisOptions);
            }
            if (pageName === "overallKpiAnalysis") {
                setApplyAllStates(overallBoxAnalysisApplyAllSelection);
            } else if (pageName === "secondaryAttributeAnalysis") {
                setApplyAllStates(secondaryBoxAnalysisApplyAllSelection);
            } else if (pageName === "agreementKpiAnalysis") {
                setApplyAllStates(agreementBoxAnalysisApplyAllSelection);
            } else if (pageName === "claimsAnalysis") {
                setApplyAllStates(claimsBoxAnalysisApplyAllSelection);
            } else if (pageName === "fragranceOverallAnalysis") {
                setApplyAllStates(fragranceOverallBoxAnalysisApplyAllSelection);
            } else if (pageName === "fragranceAdditionalAnalysis") {
                setApplyAllStates(fragranceAdditionalBoxAnalysisApplyAllSelection);
            } else if (pageName === "expressCLTOverallAnalysis") {
                setApplyAllStates(expressCltOverallBoxAnalysisApplyAllSelection);
            } else if (pageName === "expressCLTAdditionalAnalysis") {
                setApplyAllStates(expressCltAdditionalBoxAnalysisApplyAllSelection);
            }
        } else if (!value.includes("Bottom Box") || !value.includes("Top Box")) {
            setSelectedBoxAnalysisType(value);
            const boxTypeToRemove = !value.includes("Bottom Box") ? "bottom_box" : "top_box";

            setApplyAllStates((prev) => {
                const updated = {};

                for (const [key, innerState] of Object.entries(prev)) {
                    updated[key] = Object.fromEntries(Object.entries(innerState).filter(([k]) => k !== boxTypeToRemove));
                }

                return updated;
            });
        }
    };

    const handleVariableOptionChange = (variable, boxType, selectedValue, scaleKey) => {
        const { backend_variable, display_label } = variable;
        const datum = JSON.parse(JSON.stringify(selectedOptions));

        if (selectedValue) {
            setPrevSelectedBV((prev) => ({
                ...prev,
                [scaleKey]: {
                    ...(prev[scaleKey] || {}),
                    [boxType]: selectedValue,
                },
            }));
        }

        const applyAllFlag = applyAllStates?.[scaleKey]?.[boxType];

        if (applyAllFlag) {
            const scaleData = groupedVariables[scaleKey];
            scaleData.forEach((ele) => {
                const bv = ele.backend_variable;
                if (!selectedValue) {
                    if (Object.keys(datum).includes(bv)) {
                        delete datum[bv][boxType];
                        // if (Object.keys(datum?.[bv]).length === 0) {
                        //     delete datum[bv];
                        // }
                    }
                } else {
                    datum[bv] = {
                        ...datum[bv],
                        [boxType]: selectedValue,
                    };
                }
            });
        } else {
            datum[backend_variable] = {
                ...datum[backend_variable],
                [boxType]: selectedValue,
            };
            // if (isITMDataShown && pageName === "overallKpiAnalysis") {
            if (pageName === "overallKpiAnalysis") {
                const scaleData = groupedVariables[scaleKey];
                const itmRecallBv = scaleData.filter((ele) => ele.display_label === display_label);
                itmRecallBv.forEach((ele) => {
                    const bv = ele.backend_variable;
                    datum[bv] = {
                        ...datum[bv],
                        [boxType]: selectedValue,
                    };
                });
            }
            if (!selectedValue && datum[backend_variable]) {
                delete datum[backend_variable][boxType];
                // if (Object.keys(datum[backend_variable]).length === 0) {
                //     delete datum[backend_variable];
                // }
            }
        }
        setSelectedOptions({ ...datum });
    };

    // Handle Apply All toggle
    const handleApplyAllToggle = (scaleKey, boxType) => {
        const newState = !applyAllStates?.[scaleKey]?.[boxType];

        setApplyAllStates((prev) => ({
            ...prev,
            [scaleKey]: {
                ...prev[scaleKey],
                [boxType]: newState,
            },
        }));

        if (newState) {
            const boxValue = prevSelectedBV?.[scaleKey]?.[boxType];
            const updatedOptions = { ...selectedOptions };
            groupedVariables[scaleKey].forEach((variable) => {
                const bv = variable.backend_variable;
                if (!updatedOptions?.[bv] || Object.keys(updatedOptions?.[bv] ?? {}).length === 0 || !updatedOptions?.[bv]?.[boxType] || updatedOptions?.[bv]?.[boxType] === "") {
                    updatedOptions[bv] = {
                        ...updatedOptions[bv],
                        [boxType]: boxValue,
                    };
                }
            });
            setSelectedOptions(updatedOptions);
        }
    };

    // payload structure
    const handleSubmit = () => {
        clearResult();
        // * payload logic
        const _selectedBoxAnalysisType = [...selectedBoxAnalysisType].sort((a, b) => {
            if (a === "Top Box") return -1;
            if (b === "Top Box") return 1;
            return 0;
        });
        let filteredOptions;
        if (selectedBoxAnalysisType.includes("Top Box") && selectedBoxAnalysisType.includes("Bottom Box")) {
            filteredOptions = selectedOptions;
        } else {
            filteredOptions = Object.fromEntries(Object.entries(selectedOptions).map(([k, v]) => [k, selectedBoxAnalysisType.includes("Top Box") ? { top_box: v.top_box } : { bottom_box: v.bottom_box }]));
        }

        if (pageName === "overallKpiAnalysis") {
            dispatch(loadOverallAnalysisTypeValueState(analysisType));
            dispatch(loadOverallBoxAnalysisApplyAllSelectionState(applyAllStates));
            dispatch(loadOverallBoxAnalysisSelectionState(filteredOptions));
            dispatch(loadOverallBoxAnalysisTypeSelectionState(_selectedBoxAnalysisType));
        } else if (pageName === "secondaryAttributeAnalysis") {
            dispatch(loadSecondaryAnalysisTypeValueState(analysisType));
            dispatch(loadSecondaryBoxAnalysisApplyAllSelectionState(applyAllStates));
            dispatch(loadSecondaryBoxAnalysisSelectionState(filteredOptions));
            dispatch(loadSecondaryBoxAnalysisTypeSelectionState(_selectedBoxAnalysisType));
        } else if (pageName === "agreementKpiAnalysis") {
            dispatch(loadAgreementAnalysisTypeValueState(analysisType));
            dispatch(loadAgreementBoxAnalysisApplyAllSelectionState(applyAllStates));
            dispatch(loadAgreementBoxAnalysisSelectionState(filteredOptions));
            dispatch(loadAgreementBoxAnalysisTypeSelectionState(_selectedBoxAnalysisType));
        } else if (pageName === "claimsAnalysis") {
            dispatch(loadClaimsAnalysisTypeValueState(analysisType));
            dispatch(loadClaimsBoxAnalysisApplyAllSelectionState(applyAllStates));
            dispatch(loadClaimsBoxAnalysisSelectionState(filteredOptions));
            dispatch(loadClaimsBoxAnalysisTypeSelectionState(_selectedBoxAnalysisType));
        } else if (pageName === "fragranceOverallAnalysis") {
            dispatch(loadFragranceOverallTypeValueState(analysisType));
            dispatch(loadFragranceOverallBoxAnalysisApplyAllSelectionState(applyAllStates));
            dispatch(loadFragranceOverallBoxAnalysisSelectionState(filteredOptions));
            dispatch(loadFragranceOverallBoxAnalysisTypeSelectionState(_selectedBoxAnalysisType));
        } else if (pageName === "fragranceAdditionalAnalysis") {
            dispatch(loadFragranceAdditionalTypeValueState(analysisType));
            dispatch(loadFragranceAdditionalBoxAnalysisApplyAllSelectionState(applyAllStates));
            dispatch(loadFragranceAdditionalBoxAnalysisSelectionState(filteredOptions));
            dispatch(loadFragranceAdditionalBoxAnalysisTypeSelectionState(_selectedBoxAnalysisType));
        } else if (pageName === "expressCLTOverallAnalysis") {
            dispatch(loadExpressCltOverallTypeValueState(analysisType));
            dispatch(loadExpressCltOverallBoxAnalysisApplyAllSelectionState(applyAllStates));
            dispatch(loadExpressCltOverallBoxAnalysisSelectionState(filteredOptions));
            dispatch(loadExpressCltOverallBoxAnalysisTypeSelectionState(_selectedBoxAnalysisType));
        } else if (pageName === "expressCLTAdditionalAnalysis") {
            dispatch(loadExpressCltAdditionalTypeValueState(analysisType));
            dispatch(loadExpressCltAdditionalBoxAnalysisApplyAllSelectionState(applyAllStates));
            dispatch(loadExpressCltAdditionalBoxAnalysisSelectionState(filteredOptions));
            dispatch(loadExpressCltAdditionalBoxAnalysisTypeSelectionState(_selectedBoxAnalysisType));
        }

        if (filterSelectionStatus) {
            let pcs = [];
            if (Object.keys(missingFilterStoreData).length) {
                pcs = Object.keys(currentFilterStoreData)
                    .filter((item) => missingFilterStoreData[item]?.isMissing === "false")
                    .map((item) => ({
                        product: item.split("__")[0],
                        coreDataId: currentFilterStoreData[item].productCoreDataId,
                    }));
            } else {
                pcs = Object.keys(currentFilterStoreData).map((item) => ({
                    product: item.split("__")[0],
                    coreDataId: currentFilterStoreData[item].productCoreDataId,
                }));
            }

            handleFilterSubmit({
                product: null,
                coreDataId: null,
                type: "consumerFilterAll",
                consumerFilter: currentFilterStoreData,
                pcs,
                selectedStudy: null, // make sure naming is consistent
                allAnalysisData,
                fetchOnlyCurrent: false,
                boxAnalysisSelection: {
                    overall: pageName === "overallKpiAnalysis" ? selectedOptions : overallBoxAnalysisSelection,
                    secondary: pageName === "secondaryAttributeAnalysis" ? selectedOptions : secondaryBoxAnalysisSelection,
                    agreement: pageName === "agreementKpiAnalysis" ? selectedOptions : agreementBoxAnalysisSelection,
                    claims: pageName === "claimsAnalysis" ? selectedOptions : claimsBoxAnalysisSelection,
                    fragranceOverall: pageName === "fragranceOverallAnalysis" ? selectedOptions : fragranceOverallBoxAnalysisSelection,
                    fragranceAdditional: pageName === "fragranceAdditionalAnalysis" ? selectedOptions : fragranceAdditionalBoxAnalysisSelection,
                    expressCltOverall: pageName === "expressCLTOverallAnalysis" ? selectedOptions : expressCltOverallBoxAnalysisSelection,
                    expressCltAdditional: pageName === "expressCLTAdditionalAnalysis" ? selectedOptions : expressCltAdditionalBoxAnalysisSelection,
                },
                isAdditional: true,
            });
        } else {
            handleFilterSubmit({
                product: null,
                coreDataId: null,
                type: null,
                consumerFilter: null,
                pcs: [],
                selectedStudy: selectedStudies, // same key as above
                allAnalysisData: [], // keep consistent with type
                fetchOnlyCurrent: true,
                boxAnalysisSelection: {
                    overall: pageName === "overallKpiAnalysis" ? selectedOptions : overallBoxAnalysisSelection,
                    secondary: pageName === "secondaryAttributeAnalysis" ? selectedOptions : secondaryBoxAnalysisSelection,
                    agreement: pageName === "agreementKpiAnalysis" ? selectedOptions : agreementBoxAnalysisSelection,
                    claims: pageName === "claimsAnalysis" ? selectedOptions : claimsBoxAnalysisSelection,
                    fragranceOverall: pageName === "fragranceOverallAnalysis" ? selectedOptions : fragranceOverallBoxAnalysisSelection,
                    fragranceAdditional: pageName === "fragranceAdditionalAnalysis" ? selectedOptions : fragranceAdditionalBoxAnalysisSelection,
                    expressCltOverall: pageName === "expressCLTOverallAnalysis" ? selectedOptions : expressCltOverallBoxAnalysisSelection,
                    expressCltAdditional: pageName === "expressCLTAdditionalAnalysis" ? selectedOptions : expressCltAdditionalBoxAnalysisSelection,
                },
                isAdditional: true,
            });
        }
        setIsOpen(false);
    };

    const renderColumnHeaders = (scaleKey, showTopBox, showBottomBox) => {
        const scale = scaleKey?.split("_")?.[0];
        return (
            <>
                <Grid
                    container
                    spacing={2}
                    style={{
                        background: "#f0f8ff",
                        boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                        borderRadius: "10px",
                    }}
                >
                    {macroCategoryFlag && (
                        <Grid
                            item
                            xs={3}
                            style={{ color: "#335EEA", fontWeight: "bold", paddingTop: "10px", paddingBottom: "10px", fontSize: "16px" }}
                        >
                            MacroCategory
                        </Grid>
                    )}
                    <Grid
                        item
                        xs={macroCategoryFlag ? 5 : 6}
                        style={{ color: "#335EEA", fontWeight: "bold", paddingTop: "10px", paddingBottom: "10px", fontSize: "16px" }}
                    >
                        Variables with {scale} {scale !== "undefined" ? `Point` : ``} Likert Scale
                    </Grid>
                    {showTopBox && (
                        <Grid
                            item
                            xs={boxGridFlag}
                            style={{ textAlign: "center", paddingTop: "10px", paddingBottom: "10px" }}
                        >
                            <Typography style={{ color: "#335EEA", fontWeight: "bold", fontSize: "16px" }}>Top Box</Typography>
                        </Grid>
                    )}
                    {showBottomBox && (
                        <Grid
                            item
                            xs={boxGridFlag}
                            style={{ textAlign: "center", paddingTop: "10px", paddingBottom: "10px" }}
                        >
                            <Typography style={{ color: "#335EEA", fontWeight: "bold", fontSize: "16px" }}>Bottom Box</Typography>
                        </Grid>
                    )}
                </Grid>
                <Grid
                    container
                    spacing={2}
                    style={{ alignItems: "center" }}
                >
                    <Grid
                        item
                        xs={macroCategoryFlag ? 8 : 6}
                    ></Grid>
                    {showTopBox && (
                        <Grid
                            item
                            xs={boxGridFlag}
                            style={{ textAlign: "center" }}
                        >
                            <div style={{ marginTop: "5px", margin: "10px" }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={applyAllStates[scaleKey]?.["top_box"] || false}
                                            onChange={() => handleApplyAllToggle(scaleKey, "top_box")}
                                            size="small"
                                        />
                                    }
                                    label="Apply All"
                                    sx={{
                                        "& .MuiFormControlLabel-label": {
                                            fontSize: "0.65rem",
                                            color: "#335EEA",
                                        },
                                        gap: "1px",
                                    }}
                                    style={{
                                        border: "1px solid rgba(0, 0, 0, 0.2)",
                                        borderRadius: "10px",
                                        padding: "0px 6px 0px 2px",
                                    }}
                                />
                            </div>
                        </Grid>
                    )}
                    {showBottomBox && (
                        <Grid
                            item
                            xs={boxGridFlag}
                            style={{ textAlign: "center" }}
                        >
                            <div style={{ marginTop: "5px", margin: "10px" }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={applyAllStates[scaleKey]?.["bottom_box"] || false}
                                            onChange={() => handleApplyAllToggle(scaleKey, "bottom_box")}
                                            size="small"
                                        />
                                    }
                                    label="Apply All"
                                    sx={{
                                        "& .MuiFormControlLabel-label": {
                                            fontSize: "0.65rem",
                                            color: "#335EEA",
                                        },
                                        gap: "1px",
                                    }}
                                    style={{
                                        border: "1px solid rgba(0, 0, 0, 0.2)",
                                        borderRadius: "10px",
                                        padding: "0px 6px 0px 2px",
                                    }}
                                />
                            </div>
                        </Grid>
                    )}
                </Grid>
            </>
        );
    };

    const rangeFormatter = (range) => {
        return range.replace(/\((\d+)-(\d+)\)/, (_, a, b) => (Math.abs(a - b) === 1 ? `(${a}&${b})` : `(${a}-${b})`));
    };

    const createDropdown = (variable, boxType, scaleKey) => {
        const boxOptions = variable[boxType];
        const options =
            boxOptions?.map((option) => ({
                value: option.value,
                label: `${option.label} ${rangeFormatter(option.range)}`,
            })) || [];

        return (
            <Grid
                item
                xs={boxGridFlag}
                key={variable.backend_variable + boxType + scaleKey}
            >
                <ReactSelect
                    value={selectedOptions?.[variable.backend_variable]?.[boxType] || null}
                    placeholder=""
                    options={options}
                    components={{
                        IndicatorSeparator: () => null,
                    }}
                    onChange={(selectedOption) => handleVariableOptionChange(variable, boxType, selectedOption, scaleKey)}
                    styles={customStyles}
                    isSearchable={false}
                    isClearable
                />
            </Grid>
        );
    };

    const getEndLabel = (variable) => {
        const flatArr = mappings.flatMap((datum) => datum.displayLabels);
        const inRecall = flatArr.filter((ele) => ele.variableCategory === "KPI (OO)")?.some((item) => item.displayLabel === variable);
        const inItm = flatArr.filter((ele) => ele.variableCategory === "KPI ITM")?.some((item) => item.displayLabel === variable);

        if (inRecall && !inItm) {
            return "(Recall)";
        } else if (!inRecall && inItm) {
            return "(ITM)";
        } else {
            return "";
        }
    };

    const renderVariableRow = (variable, showTopBox, showBottomBox, scaleKey) => {
        const endLabel = pageName === "overallKpiAnalysis" ? getEndLabel(variable.display_label) : "";
        const variableRemoved = flatRemovedKpiList.includes(variable.backend_variable);
        if (noteFlag === "" && variableRemoved) {
            setNoteFlag("Greyed-out variables cannot be selected for box analysis as they have been removed from the analysis page. To perform box analysis, please restore them from the analysis page");
        }

        return (
            <Grid
                container
                spacing={2}
                key={variable.backend_variable}
                style={variableRemoved ? { pointerEvents: "none", opacity: 0.5 } : { marginBottom: "8px", alignItems: "center" }}
            >
                {macroCategoryFlag && (
                    <Grid
                        item
                        xs={3}
                    >
                        <Typography
                            variant="body2"
                            style={{
                                background: "#fff",
                                boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                                borderRadius: "10px",
                                padding: "6px",
                                paddingLeft: "10px",
                                fontSize: "13px",
                            }}
                        >
                            {variable.macro_category}
                        </Typography>
                    </Grid>
                )}
                <Grid
                    item
                    xs={macroCategoryFlag ? 5 : 6}
                >
                    <Typography
                        variant="body2"
                        style={{
                            background: "#fff",
                            boxShadow: "2px 2px 5px rgba(0, 0, 0, 0.2)",
                            borderRadius: "10px",
                            padding: "6px",
                            paddingLeft: "10px",
                            fontSize: "13px",
                        }}
                    >
                        {showTopBox && showBottomBox && (
                            <OverlappingScales
                                topBoxSelection={selectedOptions[variable.backend_variable]?.top_box}
                                bottomBoxSelection={selectedOptions[variable.backend_variable]?.bottom_box}
                            />
                        )}{" "}
                        {variable.display_label} {endLabel}
                    </Typography>
                </Grid>
                {showTopBox && createDropdown(variable, "top_box", scaleKey)}
                {showBottomBox && createDropdown(variable, "bottom_box", scaleKey)}
            </Grid>
        );
    };

    const renderSingleVariableGroup = (scaleKey) => {
        const group = groupedVariables[scaleKey];
        let uniqueData;
        uniqueData = Object.values(
            group.reduce((acc, item) => {
                acc[item.display_label] = item;
                return acc;
            }, {})
        );

        let sortedData;
        if (moduleName !== "claims") {
            sortedData = uniqueData.sort((a, b) => {
                const indexA = getSortIndex(a.macro_category, customOrderMacroCategory);
                const indexB = getSortIndex(b.macro_category, customOrderMacroCategory);

                return indexA - indexB;
            });
        } else {
            const claimsData = Object.keys(mappings[0]).map((item) => ({
                macroCategory: mappings[0][item].join(","),
                backendVariable: item,
            }));

            uniqueData.forEach((datum) => {
                const findInClaimsData = claimsData.find((ele) => ele.backendVariable === datum.backend_variable);
                datum.macro_category = findInClaimsData.macroCategory;
            });

            sortedData = uniqueData.sort((a, b) => {
                const labelA = a.display_label.toLowerCase();
                const labelB = b.display_label.toLowerCase();
                return labelA.localeCompare(labelB);
            });
        }
        const showTopBox = selectedBoxAnalysisType.includes("Top Box");
        const showBottomBox = selectedBoxAnalysisType.includes("Bottom Box");

        return (
            <div
                key={scaleKey}
                style={{ marginBottom: "35px" }}
            >
                {sortedData.length > 0 && renderColumnHeaders(scaleKey, showTopBox, showBottomBox)}
                {sortedData.map((variable) => renderVariableRow(variable, showTopBox, showBottomBox, scaleKey))}
            </div>
        );
    };

    const renderVariableGroups = () => {
        return Object.keys(groupedVariables).map((scaleKey) => renderSingleVariableGroup(scaleKey));
    };

    const handleCloseDialog = () => {
        setPrevSelectedBV({});
        restoreSelections();
        setIsOpen(false);
    };

    const restoreSelections = () => {
        if (pageName === "overallKpiAnalysis") {
            setSelectedOptions(overallBoxAnalysisSelection);
            setApplyAllStates(overallBoxAnalysisApplyAllSelection);
            restoreBoxAnalysisType(overallBoxAnalysisSelection);
        } else if (pageName === "secondaryAttributeAnalysis") {
            setSelectedOptions(secondaryBoxAnalysisSelection);
            setApplyAllStates(secondaryBoxAnalysisApplyAllSelection);
            restoreBoxAnalysisType(secondaryBoxAnalysisSelection);
        } else if (pageName === "agreementKpiAnalysis") {
            setSelectedOptions(agreementBoxAnalysisSelection);
            setApplyAllStates(agreementBoxAnalysisApplyAllSelection);
            restoreBoxAnalysisType(agreementBoxAnalysisSelection);
        } else if (pageName === "claimsAnalysis") {
            setSelectedOptions(claimsBoxAnalysisSelection);
            setApplyAllStates(claimsBoxAnalysisApplyAllSelection);
            restoreBoxAnalysisType(claimsBoxAnalysisSelection);
        } else if (pageName === "fragranceOverallAnalysis") {
            setSelectedOptions(fragranceOverallBoxAnalysisSelection);
            setApplyAllStates(fragranceOverallBoxAnalysisApplyAllSelection);
            restoreBoxAnalysisType(fragranceOverallBoxAnalysisSelection);
        } else if (pageName === "fragranceAdditionalAnalysis") {
            setSelectedOptions(fragranceAdditionalBoxAnalysisSelection);
            setApplyAllStates(fragranceAdditionalBoxAnalysisApplyAllSelection);
            restoreBoxAnalysisType(fragranceAdditionalBoxAnalysisSelection);
        } else if (pageName === "expressCLTOverallAnalysis") {
            setSelectedOptions(expressCltOverallBoxAnalysisSelection);
            setApplyAllStates(expressCltOverallBoxAnalysisApplyAllSelection);
            restoreBoxAnalysisType(expressCltOverallBoxAnalysisSelection);
        } else if (pageName === "expressCLTAdditionalAnalysis") {
            setSelectedOptions(expressCltAdditionalBoxAnalysisSelection);
            setApplyAllStates(expressCltAdditionalBoxAnalysisApplyAllSelection);
            restoreBoxAnalysisType(expressCltAdditionalBoxAnalysisSelection);
        }
    };

    const restoreBoxAnalysisType = (options) => {
        if (Object.keys(options).length) {
            const hasTopBox = Object.values(options).some((item) => "top_box" in item);
            const hasBottomBox = Object.values(options).some((item) => "bottom_box" in item);

            if (hasTopBox && hasBottomBox) {
                setSelectedBoxAnalysisType(["Top Box", "Bottom Box"]);
            } else if (hasTopBox && !hasBottomBox) {
                setSelectedBoxAnalysisType(["Top Box"]);
            } else if (!hasTopBox && hasBottomBox) {
                setSelectedBoxAnalysisType(["Bottom Box"]);
            } else {
                setSelectedBoxAnalysisType([]);
            }
        } else {
            setSelectedBoxAnalysisType([]);
        }
    };

    return (
        <>
            <ReactSelect
                value={value}
                placeholder={"Analysis Type"}
                options={AnalysisTypeOptions}
                onChange={handleChange}
                styles={ReactSelectStyles}
                isSearchable={false}
                isDisabled={disabled}
            />
            {isOpen && (
                <Dialog
                    open={isOpen}
                    fullWidth={true}
                    maxWidth={selectedBoxAnalysisType.length > 0 ? "md" : "sm"}
                    sx={{ "& .MuiDialog-paper": { minHeight: selectedBoxAnalysisType.length > 0 ? 450 : "auto" } }}
                >
                    <DialogTitle
                        style={{
                            backgroundColor: "rgb(51, 94, 234,0.1)",
                            color: "rgb(51, 94, 234)",
                            fontSize: 16,
                            textAlign: "center",
                            padding: "10px 12px",
                        }}
                        id="alert-dialog-title"
                    >
                        Please Select Type Of Box-Analysis
                        <IconButton
                            aria-label="close"
                            sx={{
                                position: "absolute",
                                right: 8,
                                top: 8,
                                color: "rgb(51, 94, 234) !important",
                            }}
                            onClick={handleCloseDialog}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <div style={{ padding: "20px", minHeight: selectedBoxAnalysisType.length > 0 ? 400 : "auto" }}>
                            <div style={{ marginBottom: "20px" }}>
                                <FormControl sx={{ m: 1, width: 220 }}>
                                    <MuiSelect
                                        multiple
                                        displayEmpty
                                        value={selectedBoxAnalysisType}
                                        onChange={handleBoxAnalysisTypeChange}
                                        renderValue={(selected) => {
                                            if (selected.length === 0) {
                                                return <Typography sx={{ fontSize: "11px" }}>Box Analysis Type</Typography>;
                                            }
                                            return (
                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                    {selected.map((value) => (
                                                        <Chip
                                                            key={value}
                                                            label={value}
                                                            sx={{
                                                                padding: "4px",
                                                                height: "20px",
                                                                borderRadius: "5px",
                                                                fontSize: "10px",
                                                                marginTop: "2px",
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            );
                                        }}
                                        sx={{
                                            height: 34,
                                            borderRadius: "5px",
                                            "& .MuiSelect-select": {
                                                padding: "4px 8px",
                                            },
                                        }}
                                        MenuProps={{
                                            PaperProps: {
                                                sx: {
                                                    "& .MuiMenuItem-root": {
                                                        minHeight: "24px",
                                                        padding: "0px 10px",
                                                    },
                                                    "& .MuiListItemText-primary": {
                                                        fontSize: "10px",
                                                    },
                                                    "& .MuiCheckbox-root": {
                                                        padding: "2px",
                                                        "& svg": {
                                                            fontSize: "14px",
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        <MenuItem value="all">
                                            <Checkbox
                                                checked={allSelected}
                                                indeterminate={selectedBoxAnalysisType.length > 0 && !allSelected}
                                            />
                                            <ListItemText primary="Select All" />
                                        </MenuItem>
                                        {BoxAnalysisOptions.map((option) => (
                                            <MenuItem
                                                key={option}
                                                value={option}
                                            >
                                                <Checkbox checked={selectedBoxAnalysisType.includes(option)} />
                                                <ListItemText primary={option} />
                                            </MenuItem>
                                        ))}
                                    </MuiSelect>
                                </FormControl>
                            </div>
                            {selectedBoxAnalysisType.length ? (
                                <div>
                                    <Typography style={{ fontSize: "18px", marginBottom: "30px" }}>Attribute Value & Scale</Typography>
                                    {renderVariableGroups()}
                                </div>
                            ) : (
                                <p style={{ textAlign: "center", marginTop: "35px" }}>
                                    Select at least one <i>Box Analysis Type</i> option{" "}
                                </p>
                            )}
                        </div>
                    </DialogContent>
                    <DialogActions
                        style={{
                            padding: "20px",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                        }}
                    >
                        {selectedBoxAnalysisType.length > 0 &&
                            (pageName === "overallKpiAnalysis" ? (
                                <div
                                    style={{
                                        fontSize: "12px",
                                    }}
                                >
                                    <span
                                        style={{
                                            fontWeight: "bold",
                                        }}
                                    >
                                        Note:&nbsp;
                                        <br />
                                    </span>
                                    <ul>
                                        <li>
                                            <span>If ITM is activated, please note that Top Box and/or Bottom Box analysis will be applied to both ITM and Recall</span>
                                        </li>
                                        {noteFlag && <li>{noteFlag}</li>}
                                    </ul>
                                </div>
                            ) : (
                                <>
                                    <p
                                        style={{
                                            fontSize: "12px",
                                        }}
                                    >
                                        <span
                                            style={{
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {noteFlag ? "Note:" : ""}
                                            <br />
                                        </span>
                                        <span>{noteFlag}</span>
                                    </p>
                                    {(pageName === "fragranceAdditionalAnalysis" || pageName === "fragranceOverallAnalysis") && (
                                        <p
                                            style={{
                                                fontSize: "12px",
                                                marginLeft: "-325px",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {"Note:"}
                                                <br />
                                            </span>
                                            <span>{"The box analysis options for JAR are pre-defined (Bottom 2 Box, Middle Box and Top 2 Box)"}</span>
                                        </p>
                                    )}
                                </>

                            ))}
                        <Button
                            size="small"
                            className={disableGetResultsFlag ? "custom-btn" : "custom-btn custom-btn-outlined mx-2"}
                            variant="outlined"
                            onClick={handleSubmit}
                            disabled={disableGetResultsFlag}
                        >
                            Get Results
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </>
    );
};

// Typechecking props for the AnalysisTypeSelector
AnalysisTypeSelector.propTypes = {
    value: PropTypes.string,
    pageName: PropTypes.string,
    variableCategory: PropTypes.string,
    mappings: PropTypes.array,
    handleFilterSubmit: PropTypes.func,
    moduleName: PropTypes.string,
    clearResult: PropTypes.func,
    disabled: PropTypes.bool,
};

export default AnalysisTypeSelector;
